import { ChevronLeft, ChevronRight, Plus, SlidersHorizontal } from 'lucide-react';

import {
  formatAnchorLabel,
  type ScheduleView,
} from './scheduleDateUtils';

interface ScheduleToolbarProps {
  view: ScheduleView;
  anchor: Date;
  activeFilterCount: number;
  isOwner: boolean;
  onViewChange: (view: ScheduleView) => void;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onOpenFilters: () => void;
  onCreateClass?: () => void;
}

const VIEW_OPTIONS: { value: ScheduleView; label: string }[] = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

export function ScheduleToolbar({
  view,
  anchor,
  activeFilterCount,
  isOwner,
  onViewChange,
  onPrev,
  onNext,
  onToday,
  onOpenFilters,
  onCreateClass,
}: ScheduleToolbarProps) {
  return (
    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
      {/* Left: navigation */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={onToday}
          className="text-sm text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#221F1F] hover:bg-[#363232] border border-[#504A4A] px-3 py-1.5 rounded-lg transition-colors"
        >
          Today
        </button>
        <button
          onClick={onPrev}
          className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
          aria-label="Previous"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={onNext}
          className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
          aria-label="Next"
        >
          <ChevronRight size={18} />
        </button>
        <span className="text-[#EBFFEE] font-medium text-sm whitespace-nowrap truncate">
          {formatAnchorLabel(view, anchor)}
        </span>
      </div>

      {/* Right: view switcher, filters, new class */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* View segmented control */}
        <div className="inline-flex rounded-lg bg-[#221F1F] border border-[#504A4A] overflow-hidden">
          {VIEW_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onViewChange(opt.value)}
              className={`text-xs font-medium px-3 py-1.5 transition-colors ${
                view === opt.value
                  ? 'bg-[#9B1421] text-white'
                  : 'text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filter button */}
        <button
          onClick={onOpenFilters}
          className={`relative flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors border ${
            activeFilterCount > 0
              ? 'text-[#EBFFEE] bg-[#9B1421]/20 border-[#9B1421]/60 hover:bg-[#9B1421]/30'
              : 'text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#221F1F] hover:bg-[#363232] border-[#504A4A]'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="text-[10px] bg-[#9B1421] text-white px-1.5 py-0.5 rounded-full font-semibold leading-none">
              {activeFilterCount}
            </span>
          )}
        </button>

        {isOwner && onCreateClass && (
          <button
            onClick={onCreateClass}
            className="flex items-center gap-1.5 bg-[#9B1421] hover:bg-[#7A0F1A] text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors"
          >
            <Plus size={15} />
            New Class
          </button>
        )}
      </div>
    </div>
  );
}
